import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { Calendar, User, ArrowLeft, FileText, Image as ImageIcon, ExternalLink, Paperclip } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="blog-post-page">
        <div className="container mx-auto px-6">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}
          {!loading && !post && (
            <div className="text-center py-12" data-testid="post-not-found">
              <p className="text-[#5C665A]">Article non trouvé.</p>
              <Link to="/blog" className="text-[#4A6B48] hover:underline mt-4 inline-block">
                Retour au blog
              </Link>
            </div>
          )}
          {!loading && post && (
            <article className="max-w-4xl mx-auto" data-testid="blog-post-content">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-[#4A6B48] hover:underline mb-6"
                data-testid="back-to-blog"
              >
                <ArrowLeft size={16} className="mr-2" />
                Retour au blog
              </Link>

              {post.image_url && (
                <img 
                  src={post.image_url} 
                  alt={post.title}
                  className="w-full h-96 object-cover rounded-lg mb-8"
                />
              )}

              <div className="flex items-center space-x-6 text-sm text-[#5C665A] mb-6">
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>{new Date(post.created_at).toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              </div>

              <h1 
                className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-6 leading-tight"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
                data-testid="post-title"
              >
                {post.title}
              </h1>

              <div 
                className="prose prose-lg max-w-none text-[#2E332D] leading-relaxed"
                data-testid="post-body"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {post.content}
              </div>

              {post.attachments && post.attachments.length > 0 && (
                <div className="mt-10 border-t border-[#E5E3DB] pt-6" data-testid="post-attachments">
                  <h2 className="text-xl font-bold text-[#2E332D] mb-4 inline-flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    <Paperclip size={20} className="text-[#4A6B48]" />
                    Pièces jointes ({post.attachments.length})
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {post.attachments.map((a, idx) => {
                      const isImage = a.file_type === 'image';
                      const url = a.url && a.url.startsWith('http') ? a.url : `${BACKEND_URL}${a.url}`;
                      return (
                        <a
                          key={`${a.url}-${idx}`}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`attachment-${idx}`}
                          className="flex items-center gap-3 p-3 bg-[#F9F8F6] hover:bg-[#4A6B48]/5 border border-[#E5E3DB] rounded-lg transition-colors"
                        >
                          <div className="w-10 h-10 rounded bg-white flex items-center justify-center shrink-0">
                            {isImage ? (
                              <ImageIcon size={20} className="text-[#D87D4A]" />
                            ) : (
                              <FileText size={20} className="text-[#4A6B48]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-[#2E332D] truncate">{a.title}</p>
                            <p className="text-xs text-[#5C665A] uppercase tracking-wide">{a.file_type}</p>
                          </div>
                          <ExternalLink size={14} className="text-[#4A6B48] shrink-0" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
